import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getIsAuthenticated, getUser } from "@/src/auth/store/auth-selectors";
import { logger } from "@/src/common/lib/logger";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { scheduleIdle } from "@/src/common/utils/schedule-idle";
import { getActiveCommunityId } from "@/src/community/store/community-selectors";
import { type CometChatSdk, loadCometChatSdk } from "@/src/message/lib/cometchat-sdk";
import { useGetCometChatTokenMutation, useGetCommunityCometchatMembersQuery } from "@/src/message/services/message-http.service";
import { setUnreadCount } from "@/src/message/store/message-slice";

interface CometChatContextValue {
	isReady: boolean;
	isMembersLoading: boolean;
	communityMemberUids: string[];
	cometchatGroupId: string | null;
	subGroups: Array<{ group_id: string; cometchat_group_id: string; name: string }>;
	adhocGroupGuids: string[];
	addAdhocGroupGuid: (guid: string) => void;
}

const CometChatContext = createContext<CometChatContextValue>({
	isReady: false,
	isMembersLoading: false,
	communityMemberUids: [],
	cometchatGroupId: null,
	subGroups: [],
	adhocGroupGuids: [],
	addAdhocGroupGuid: () => {},
});

export function useCometChat() {
	return useContext(CometChatContext);
}

const LISTENER_ID = "global-message-listener";

export function CometChatProvider({ children }: { children: React.ReactNode }) {
	const dispatch = useAppDispatch();
	const isAuthenticated = useAppSelector(getIsAuthenticated);
	const user = useAppSelector(getUser);
	const activeCommunityId = useAppSelector(getActiveCommunityId);
	const [sdk, setSdk] = useState<CometChatSdk | null>(null);
	const [isReady, setIsReady] = useState(false);
	const [adhocGroupGuids, setAdhocGroupGuids] = useState<string[]>(() => {
		try {
			const stored = localStorage.getItem("upsquad.cometchat.adhoc_guids");
			return stored ? (JSON.parse(stored) as string[]) : [];
		} catch {
			return [];
		}
	});
	const [getCometChatToken] = useGetCometChatTokenMutation();
	const loggedInRef = useRef(false);

	const { data: cometchatMembersData, isLoading: isMembersLoading } = useGetCommunityCometchatMembersQuery(activeCommunityId ?? "", {
		skip: !activeCommunityId || !isReady,
	});

	const communityMemberUids = useMemo(() => cometchatMembersData?.member_uids ?? [], [cometchatMembersData]);
	const cometchatGroupId = cometchatMembersData?.cometchat_group_id ?? null;
	const subGroups = useMemo(() => {
		const groups = cometchatMembersData?.groups;
		if (!groups) return [];
		return groups.filter((g): g is { group_id: string; cometchat_group_id: string; name: string } => g.cometchat_group_id !== null);
	}, [cometchatMembersData]);

	useEffect(() => {
		if (!isAuthenticated) return;
		let cancelled = false;

		const cancelIdle = scheduleIdle(() => {
			loadCometChatSdk()
				.then((loaded) => {
					if (!cancelled) setSdk(loaded);
				})
				.catch((error: unknown) => {
					logger.error("[CometChat] Initialization failed", error);
				});
		});

		return () => {
			cancelled = true;
			cancelIdle();
		};
	}, [isAuthenticated]);

	useEffect(() => {
		if (!sdk || !isAuthenticated || !user?.cometchat_uid || loggedInRef.current) return;
		let cancelled = false;

		const loginWithToken = async () => {
			try {
				const loggedInUser = await sdk.CometChatUIKit.getLoggedInUser();
				if (cancelled) return;

				if (!loggedInUser) {
					const { token } = await getCometChatToken().unwrap();
					if (cancelled) return;

					await sdk.CometChatUIKit.loginWithAuthToken(token);
					if (cancelled) return;
				}

				loggedInRef.current = true;
				setIsReady(true);
			} catch (error) {
				logger.error("[CometChat] Login failed", error);
			}
		};

		loginWithToken();

		return () => {
			cancelled = true;
		};
	}, [sdk, isAuthenticated, user?.cometchat_uid, getCometChatToken]);

	useEffect(() => {
		if (!sdk || !isReady) return;

		const { CometChat } = sdk;
		const memberUidSet = new Set(communityMemberUids);
		const groupGuidSet = new Set([cometchatGroupId, ...subGroups.map((g) => g.cometchat_group_id), ...adhocGroupGuids].filter(Boolean));

		const updateUnreadCount = async () => {
			try {
				const counts: { users?: Record<string, number>; groups?: Record<string, number> } = await CometChat.getUnreadMessageCount();
				const userCounts = counts.users ?? {};
				const groupCounts = counts.groups ?? {};

				let total = 0;
				for (const [uid, count] of Object.entries(userCounts)) {
					if (memberUidSet.size === 0 || memberUidSet.has(uid)) total += count;
				}
				for (const [guid, count] of Object.entries(groupCounts)) {
					if (groupGuidSet.size === 0 || groupGuidSet.has(guid)) total += count;
				}

				dispatch(setUnreadCount(total));
			} catch {
				// silently fail
			}
		};

		updateUnreadCount();

		CometChat.addMessageListener(
			LISTENER_ID,
			new CometChat.MessageListener({
				onTextMessageReceived: () => updateUnreadCount(),
				onMediaMessageReceived: () => updateUnreadCount(),
				onCustomMessageReceived: () => updateUnreadCount(),
			}),
		);

		return () => {
			CometChat.removeMessageListener(LISTENER_ID);
		};
	}, [sdk, isReady, dispatch, communityMemberUids, cometchatGroupId, subGroups, adhocGroupGuids]);

	useEffect(() => {
		if (!sdk || isAuthenticated || !loggedInRef.current) return;

		sdk.CometChatUIKit.logout().catch(() => {});
		loggedInRef.current = false;
		setIsReady(false);
		dispatch(setUnreadCount(0));
	}, [sdk, isAuthenticated, dispatch]);

	const addAdhocGroupGuid = useCallback((guid: string) => {
		setAdhocGroupGuids((prev) => {
			if (prev.includes(guid)) return prev;
			const next = [...prev, guid];
			try {
				localStorage.setItem("upsquad.cometchat.adhoc_guids", JSON.stringify(next));
			} catch {}
			return next;
		});
	}, []);

	const contextValue = useMemo(
		() => ({ isReady, isMembersLoading, communityMemberUids, cometchatGroupId, subGroups, adhocGroupGuids, addAdhocGroupGuid }),
		[isReady, isMembersLoading, communityMemberUids, cometchatGroupId, subGroups, adhocGroupGuids, addAdhocGroupGuid],
	);

	return <CometChatContext.Provider value={contextValue}>{children}</CometChatContext.Provider>;
}
